import { FC, useState, useEffect, useCallback } from "react";
import { Button, Form, Spinner, Modal } from "react-bootstrap";
import Loader from "src/components/common/loader/loader";
import type { Role } from "@services/permissions/commands/cmdListRoles";
import type { RoleDetail } from "@services/permissions/commands/cmdGetRole";
import type { Feature } from "@services/permissions/commands/cmdCreateFeature";
import ModalDelete from "../../../components/common/ModalDelete";
import showAlert from "src/shared/utils/generic.tsx";

const LEVELS = [
  { label: "View", value: 1 },
  { label: "Update", value: 2 },
  { label: "Create", value: 4 },
  { label: "Delete", value: 8 },
] as const;

interface FlatFeature {
  feature: Feature;
  depth: number;
}

function flattenTree(features: Feature[]): FlatFeature[] {
  const childrenMap = new Map<number | null, Feature[]>();
  for (const f of features) {
    const key = f.parent_id ?? null;
    if (!childrenMap.has(key)) childrenMap.set(key, []);
    childrenMap.get(key)!.push(f);
  }
  const result: FlatFeature[] = [];
  function walk(parentId: number | null, depth: number) {
    const children = childrenMap.get(parentId) || [];
    for (const child of children) {
      result.push({ feature: child, depth });
      walk(child.id, depth + 1);
    }
  }
  walk(null, 0);
  return result;
}

interface Props {
  roles: Role[];
  features: Feature[];
  isLoading: boolean;
  onCreate: (name: string) => Promise<void>;
  onRename: (id: number, name: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onGetRole: (id: number) => Promise<RoleDetail>;
  onSavePermissions: (roleId: number, permissions: { feature_id: number; level: number }[]) => Promise<void>;
}

const RolesSection: FC<Props> = ({
  roles, features, isLoading,
  onCreate, onRename, onDelete, onGetRole, onSavePermissions,
}) => {
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [permMap, setPermMap] = useState<Map<number, number>>(new Map());
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [savingPerms, setSavingPerms] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // Rename modal
  const [renameTarget, setRenameTarget] = useState<Role | null>(null);
  const [renameName, setRenameName] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const flat = flattenTree(features);

  const loadPermissions = useCallback(async (roleId: number) => {
    setLoadingPerms(true);
    try {
      const detail = await onGetRole(roleId);
      const map = new Map<number, number>();
      for (const p of detail.permissions) map.set(p.feature_id, p.level);
      setPermMap(map);
      setDirty(false);
    } catch {
      showAlert("Failed to load role permissions", "error", 3000);
    } finally {
      setLoadingPerms(false);
    }
  }, [onGetRole]);

  useEffect(() => {
    if (selectedRoleId) loadPermissions(selectedRoleId);
  }, [selectedRoleId, loadPermissions]);

  // Deselect role if it's deleted
  useEffect(() => {
    if (selectedRoleId && !roles.find((r) => r.id === selectedRoleId)) {
      setSelectedRoleId(null);
    }
  }, [roles, selectedRoleId]);

  const toggleLevel = (featureId: number, levelBit: number) => {
    setPermMap((prev) => {
      const next = new Map(prev);
      const current = next.get(featureId) || 0;
      next.set(featureId, current ^ levelBit);
      return next;
    });
    setDirty(true);
  };

  const handleSavePermissions = async () => {
    if (!selectedRoleId) return;
    setSavingPerms(true);
    try {
      const permissions: { feature_id: number; level: number }[] = [];
      for (const [featureId, level] of permMap) {
        if (level > 0) permissions.push({ feature_id: featureId, level });
      }
      await onSavePermissions(selectedRoleId, permissions);
      setDirty(false);
    } catch {
      showAlert("Failed to save permissions", "error", 3000);
    } finally {
      setSavingPerms(false);
    }
  };

  const handleCreate = async () => {
    if (!createName.trim()) return;
    setIsCreating(true);
    try {
      await onCreate(createName.trim());
      setCreateName("");
      setShowCreateModal(false);
    } catch {
      showAlert("Failed to create role", "error", 3000);
    } finally {
      setIsCreating(false);
    }
  };

  const handleRename = async () => {
    if (!renameTarget || !renameName.trim()) return;
    setIsRenaming(true);
    try {
      await onRename(renameTarget.id, renameName.trim());
      setRenameTarget(null);
    } catch {
      showAlert("Failed to rename role", "error", 3000);
    } finally {
      setIsRenaming(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await onDelete(deleteTarget.id);
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  const selectedRole = roles.find((r) => r.id === selectedRoleId);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <div className="roles-layout">
        {/* Left: role list */}
        <div className="roles-layout__list">
          <div className="roles-layout__list-header">
            <span>Roles List</span>
            <Button
              variant="primary"
              size="sm"
              onClick={() => { setCreateName(""); setShowCreateModal(true); }}
            >
              <i className="bi bi-plus-circle me-1" />New Role
            </Button>
          </div>
          <div className="roles-layout__list-body">
            {roles.length === 0 ? (
              <div className="text-center py-4 text-muted small">
                No roles yet.
              </div>
            ) : (
              roles.map((role) => (
                <button
                  key={role.id}
                  type="button"
                  className={`roles-layout__item ${selectedRoleId === role.id ? "roles-layout__item--active" : ""}`}
                  onClick={() => setSelectedRoleId(role.id)}
                >
                  <span className="roles-layout__item-name">{role.name}</span>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right: permission matrix or empty state */}
        <div className="roles-layout__detail">
          {!selectedRole ? (
            <div className="roles-layout__detail-header">
              <span>Role Detail</span>
            </div>
          ) : (
            <div className="roles-layout__detail-header">
              <span>{selectedRole.name}</span>
              <div className="d-flex gap-1">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => { setRenameTarget(selectedRole); setRenameName(selectedRole.name); }}
                  title="Rename"
                >
                  <i className="bi bi-pencil" />
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => setDeleteTarget({ id: selectedRole.id, name: selectedRole.name })}
                  title="Delete"
                >
                  <i className="bi bi-trash" />
                </Button>
              </div>
            </div>
          )}

          {!selectedRole ? (
            <div className="roles-layout__empty">
              <i className="bi bi-shield-lock fs-1 d-block mb-2 opacity-25" />
              <p className="text-muted mb-0">
                {roles.length === 0
                  ? "Create a role to configure permissions."
                  : "Select a role to edit its permissions."}
              </p>
            </div>
          ) : loadingPerms ? (
            <Loader />
          ) : features.length === 0 ? (
            <p className="text-muted text-center py-3 small">
              No features defined. Create features first.
            </p>
          ) : (
            <>
              <div className="perm-matrix">
                <table className="perm-matrix__table">
                  <thead>
                    <tr>
                      <th className="perm-matrix__feature-col">Feature</th>
                      {LEVELS.map((l) => (
                        <th key={l.value} className="perm-matrix__level-col">{l.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {flat.map(({ feature, depth }) => {
                      const level = permMap.get(feature.id) || 0;
                      return (
                        <tr key={feature.id}>
                          <td
                            className="perm-matrix__feature-name"
                            style={{ paddingLeft: 12 + depth * 20 }}
                          >
                            {depth > 0 && (
                              <span className="perm-matrix__indent">└</span>
                            )}
                            {feature.name}
                          </td>
                          {LEVELS.map((l) => (
                            <td key={l.value} className="perm-matrix__check-cell">
                              <Form.Check
                                type="checkbox"
                                checked={(level & l.value) !== 0}
                                onChange={() => toggleLevel(feature.id, l.value)}
                                id={`perm-${selectedRole.id}-${feature.id}-${l.value}`}
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="roles-layout__detail-footer">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSavePermissions}
                  disabled={savingPerms || !dirty}
                >
                  {savingPerms ? (
                    <><Spinner animation="border" size="sm" className="me-1" /> Saving...</>
                  ) : (
                    <><i className="bi bi-check-circle me-1" />Save Permissions</>
                  )}
                </Button>
                {dirty && (
                  <span className="text-muted small ms-2">Unsaved changes</span>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create role modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h5">New Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label className="fw-semibold">Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder="e.g. Editor"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowCreateModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleCreate} disabled={isCreating || !createName.trim()}>
            {isCreating ? (
              <><Spinner animation="border" size="sm" className="me-1" /> Creating...</>
            ) : (
              <><i className="bi bi-check-circle me-1" />Create Role</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Rename role modal */}
      <Modal show={renameTarget !== null} onHide={() => setRenameTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Rename Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label className="fw-semibold">Name</Form.Label>
            <Form.Control
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") handleRename(); }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setRenameTarget(null)}>Cancel</Button>
          <Button variant="primary" onClick={handleRename} disabled={isRenaming || !renameName.trim()}>
            {isRenaming ? (
              <><Spinner animation="border" size="sm" className="me-1" /> Saving...</>
            ) : (
              <><i className="bi bi-check-circle me-1" />Save</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <ModalDelete
        title="Role"
        objectLabel={deleteTarget?.name || ""}
        deleteFn={handleDelete}
        visible={deleteTarget !== null}
        toggleVisibility={(v: boolean) => { if (!v) setDeleteTarget(null); }}
        isLoading={isDeleting}
      />
    </>
  );
};

export default RolesSection;
